import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { invoicesAPI, clientsAPI, inventoryAPI, taxesAPI, accountStatusesAPI, quotesAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { ArrowLeft, Edit, Download, Send, Plus, X, DollarSign, Clock, CheckCircle, XCircle, FileEdit, CreditCard, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { InvalidRoute, isValidId } from '../components/ui/page-guards';
import { format } from 'date-fns';
import { numberToWords } from '../utils/numberToWords';
import { jsPDF } from 'jspdf';
import { useCurrency } from '../contexts/CurrencyContext';
import { formatCurrency, getCurrencySymbol, formatWithSymbol } from '../utils/currency';

const statusColors = {
  draft: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200',
  sent: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  paid: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  overdue: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  cancelled: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200',
};

// Tax presets
const TAX_PRESETS = {
  india: [
    { name: 'CGST 9%', rate: 9 },
    { name: 'SGST 9%', rate: 9 },
    { name: 'IGST 18%', rate: 18 },
    { name: 'GST 5%', rate: 5 },
    { name: 'GST 12%', rate: 12 },
    { name: 'GST 28%', rate: 28 },
  ],
  usa: [
    { name: 'Sales Tax 6%', rate: 6 },
    { name: 'Sales Tax 7%', rate: 7 },
    { name: 'Sales Tax 8%', rate: 8 },
    { name: 'Sales Tax 9%', rate: 9 },
    { name: 'Sales Tax 10%', rate: 10 },
  ],
};

// Mock inventory
const INVENTORY_ITEMS = [
  { id: 1, name: 'CRM Software License', price: 999, sku: 'CRM-001' },
  { id: 2, name: 'Mobile App Integration', price: 1499, sku: 'MOB-001' },
  { id: 3, name: 'Premium Support Package', price: 299, sku: 'SUP-001' },
  { id: 4, name: 'Training Session', price: 199, sku: 'TRN-001' },
  { id: 5, name: 'Custom Integration', price: 2999, sku: 'INT-001' },
];

export default function InvoiceDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const baseCurrency = useCurrency();
  
  // Route guard - validate ID before any operations
  if (!isValidId(id)) {
    return (
      <InvalidRoute
        title="Invalid Invoice"
        message="The invoice ID is missing or invalid. Please select a valid invoice from the list."
        backPath="/invoices"
        backLabel="Back to Invoices"
      />
    );
  }
  
  const [invoice, setInvoice] = useState(null);
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editSheetOpen, setEditSheetOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [lineItems, setLineItems] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [availableInventory, setAvailableInventory] = useState([]);
  
  // Payment state
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    payment_method: 'cash',
    payment_date: format(new Date(), 'yyyy-MM-dd'),
    notes: ''
  });
  const [payments, setPayments] = useState([]);
  const [activities, setActivities] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [invoiceStatuses, setInvoiceStatuses] = useState([]);

  // Document-aware currency: render in the invoice's OWN snapshotted currency
  // (fallback to the user's currency for legacy invoices saved before this field).
  const docCurrencyCode = invoice?.currency || baseCurrency.currencyCode || 'USD';
  const currency = {
    currencyCode: docCurrencyCode,
    symbol: getCurrencySymbol(docCurrencyCode),
    format: (amount, options = {}) => formatCurrency(amount, docCurrencyCode, options),
    formatSymbol: (amount) => formatWithSymbol(amount, docCurrencyCode),
  };

  // Check if user can update invoices
  const canUpdate = () => {
    if (!user?.module_permissions) return true; // Admin
    return hasPermission('account', 'invoice', 'update');
  };

  useEffect(() => {
    loadInvoice();
  }, [id]);

  const loadInvoice = async () => {
    try {
      const response = await invoicesAPI.getOne(id);
      const invoiceData = response.data;
      setInvoice(invoiceData);
      
      // Load taxes from API
      try {
        const taxesResponse = await taxesAPI.getAll();
        const taxData = taxesResponse.data?.items || taxesResponse.data || [];
        setTaxes(Array.isArray(taxData) ? taxData : []);
      } catch (error) {
        console.error('Failed to load taxes:', error);
        setTaxes([]);
      }
      
      // Load invoice statuses from API
      try {
        const statusesResponse = await accountStatusesAPI.getInvoiceStatuses();
        setInvoiceStatuses(Array.isArray(statusesResponse.data) ? statusesResponse.data : []);
      } catch (error) {
        console.error('Failed to load invoice statuses:', error);
        setInvoiceStatuses([]);
      }
      
      // Load payments from backend
      try {
        const paymentsResponse = await invoicesAPI.getPayments(id);
        setPayments(Array.isArray(paymentsResponse.data) ? paymentsResponse.data : []);
      } catch (error) {
        console.error('Failed to load payments:', error);
        setPayments([]);
      }
      
      // Load activities from backend
      try {
        const activitiesResponse = await invoicesAPI.getActivities(id);
        if (Array.isArray(activitiesResponse.data) && activitiesResponse.data.length > 0) {
          setActivities(activitiesResponse.data);
        } else {
          // If no activities exist, create default "created" activity
          setActivities([
            {
              id: 1,
              type: 'created',
              description: `Invoice created`,
              timestamp: invoiceData.created_at || new Date().toISOString()
            }
          ]);
        }
      } catch (error) {
        console.error('Failed to load activities:', error);
        // Fallback to default activity
        setActivities([
          {
            id: 1,
            type: 'created',
            description: `Invoice created`,
            timestamp: invoiceData.created_at || new Date().toISOString()
          }
        ]);
      }

      // Load client details
      if (invoiceData.client_id) {
        try {
          const clientRes = await clientsAPI.getOne(invoiceData.client_id);
          setClient(clientRes.data);
        } catch (error) {
          console.error('Failed to load client:', error);
        }
      }
      
      // Load linked quote payments if quote_id exists
      if (invoiceData.quote_id) {
        try {
          const quoteResponse = await quotesAPI.getOne(invoiceData.quote_id);
          const quoteData = quoteResponse.data;
          // Store quote payment info for display
          setInvoice(prev => ({
            ...prev,
            quote_paid_amount: quoteData.paid_amount || 0,
            quote_number: quoteData.quote_number
          }));
        } catch (error) {
          console.error('Failed to load quote:', error);
        }
      }
    } catch (error) {
      console.error('Failed to load invoice:', error);
      toast.error('Failed to load invoice details');
      setInvoice(null);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async () => {
    // Load inventory items from API
    try {
      const inventoryResponse = await inventoryAPI.getItems();
      if (inventoryResponse.data && Array.isArray(inventoryResponse.data)) {
        setAvailableInventory(inventoryResponse.data);
      }
    } catch (error) {
      console.error('Failed to load inventory:', error);
      toast.error('Failed to load inventory items');
    }

    // Pre-fill edit form with current invoice data
    setEditForm({
      client_id: invoice.client_id || '',
      invoice_number: invoice.invoice_number || '',
      invoice_date: invoice.invoice_date || format(new Date(), 'yyyy-MM-dd'),
      expiry_date: invoice.expiry_date || '',
      status: invoice.status || 'draft',
      discount_type: invoice.discount_type || 'percentage',
      discount_value: invoice.discount_value || 0,
      tax_jurisdiction: invoice.tax_jurisdiction || 'usa',
      tax_rate: invoice.tax_rate || 0,
      tax_name: invoice.tax_name || '',
      deposit_required: invoice.deposit_required || 0,
      instructions: invoice.instructions || '',
      notes: invoice.notes || '',
      terms: invoice.terms || '',
    });
    setLineItems(invoice.line_items || []);
    setInventoryItems(invoice.inventory_items || []);
    setEditSheetOpen(true);
  };

  const handleUpdateInvoice = async (e) => {
    e.preventDefault();
    
    const grandTotal = calculateGrandTotal();
    const invoiceData = {
      ...editForm,
      line_items: lineItems,
      inventory_items: inventoryItems,
      subtotal: calculateSubtotal(),
      tax_amount: calculateTax(),
      discount_amount: calculateDiscount(),
      total_amount: grandTotal,
      total_in_words: numberToWords(grandTotal, currency.currencyCode)
    };

    try {
      await invoicesAPI.update(id, invoiceData);
      toast.success('Invoice updated successfully');
      
      // Save activity to backend
      const activityData = {
        invoice_id: id,
        activity_type: 'edit',
        description: `Invoice updated - Total: ${currency.format(grandTotal)}`,
        metadata: {
          total_amount: grandTotal
        }
      };
      
      try {
        await invoicesAPI.createActivity(id, activityData);
      } catch (error) {
        console.error('Failed to save activity:', error);
      }
      
      setEditSheetOpen(false);
      loadInvoice(); // Reload the invoice
    } catch (error) {
      console.error('Failed to update invoice:', error);
      toast.error('Failed to update invoice');
    }
  };

  // Line Items Management
  const handleAddLineItem = () => {
    setLineItems([...lineItems, {
      id: Date.now(),
      description: '',
      quantity: 1,
      price: 0,
      tax: 0,
      total: 0
    }]);
  };

  const handleRemoveLineItem = (itemId) => {
    setLineItems(lineItems.filter(item => item.id !== itemId));
  };

  const handleUpdateLineItem = (itemId, field, value) => {
    setLineItems(lineItems.map(item => {
      if (item.id === itemId) {
        const updated = { ...item, [field]: value };
        const subtotal = (updated.quantity || 0) * (updated.price || 0);
        updated.total = subtotal + (updated.tax || 0);
        return updated;
      }
      return item;
    }));
  };

  // Inventory Items Management
  const handleAddInventoryItem = () => {
    setInventoryItems([...inventoryItems, {
      id: Date.now(),
      itemId: '',
      quantity: 1,
      price: 0,
      taxRate: 0,
      tax: 0,
      total: 0
    }]);
  };

  const handleRemoveInventoryItem = (itemId) => {
    setInventoryItems(inventoryItems.filter(item => item.id !== itemId));
  };

  const handleUpdateInventoryItem = (itemId, field, value) => {
    setInventoryItems(inventoryItems.map(item => {
      if (item.id === itemId) {
        const updated = { ...item, [field]: value };
        
        if (field === 'itemId') {
          const invItem = availableInventory.find(i => i.id === value);
          if (invItem) {
            updated.price = invItem.selling_price || invItem.price || 0;
          }
        }
        
        const subtotal = (updated.quantity || 0) * (updated.price || 0);
        updated.tax = subtotal * (updated.taxRate || 0) / 100;
        updated.total = subtotal + updated.tax;
        return updated;
      }
      return item;
    }));
  };

  // Calculate Totals
  const calculateSubtotal = () => {
    const lineItemsTotal = lineItems.reduce((sum, item) => sum + ((item.quantity || 0) * (item.price || 0)), 0);
    const inventoryTotal = inventoryItems.reduce((sum, item) => sum + ((item.quantity || 0) * (item.price || 0)), 0);
    return lineItemsTotal + inventoryTotal;
  };

  const calculateTax = () => {
    const lineItemsTax = lineItems.reduce((sum, item) => sum + (item.tax || 0), 0);
    const inventoryTax = inventoryItems.reduce((sum, item) => sum + (item.tax || 0), 0);
    const subtotal = calculateSubtotal();
    const additionalTax = subtotal * (editForm.tax_rate || 0) / 100;
    return lineItemsTax + inventoryTax + additionalTax;
  };

  const calculateDiscount = () => {
    const subtotal = calculateSubtotal();
    if (editForm.discount_type === 'percentage') {
      return subtotal * (editForm.discount_value || 0) / 100;
    }
    return editForm.discount_value || 0;
  };

  const calculateGrandTotal = () => {
    const subtotal = calculateSubtotal();
    const tax = calculateTax();
    const discount = calculateDiscount();
    return subtotal + tax - discount;
  };

  const handleStatusChange = async (newStatus) => {
    try {
      // Update status
      await invoicesAPI.update(id, { status: newStatus });
      setInvoice({ ...invoice, status: newStatus });
      
      // Save activity to backend
      const activityData = {
        invoice_id: id,
        activity_type: 'status_change',
        description: `Status changed from "${invoice.status}" to "${newStatus}"`,
        metadata: {
          old_status: invoice.status,
          new_status: newStatus
        }
      };
      
      try {
        const activityResponse = await invoicesAPI.createActivity(id, activityData);
        // Add the saved activity to local state
        setActivities([...activities, activityResponse.data]);
      } catch (error) {
        console.error('Failed to save activity:', error);
      }
      
      // If status is 'sent', also send email to client
      if (newStatus === 'sent') {
        try {
          const response = await api.post(`/api/invoices/${id}/send-email`);
          toast.success(`Invoice sent to client successfully (${response.data.sent_to})`);
        } catch (emailError) {
          console.error('Failed to send email:', emailError);
          const detail = emailError.response?.data?.detail;
          toast.error(`Status updated, but email failed: ${typeof detail === 'string' ? detail : (detail?.message || 'Unknown error')}`);
        }
      } else {
        toast.success(`Invoice status updated to ${newStatus}`);
      }
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update status');
    }
  };
  
  const handleDownloadPDF = () => {
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;
      let yPos = 20;
      // jsPDF's default font can't render the ₹ glyph, so use ASCII "Rs." for INR.
      const pdfSym = currency.currencyCode === 'INR' ? 'Rs. ' : (currency.symbol || '$');
      const isINR = currency.currencyCode === 'INR';
      // Company logo (top-left). Base64 data URL is CORS-safe and synchronous; guard against bad data.
      if (user?.company_logo) {
        try {
          const props = doc.getImageProperties(user.company_logo);
          const maxH = 16, maxW = 45;
          let w = maxW, h = (props.height / props.width) * maxW;
          if (h > maxH) { h = maxH; w = (props.width / props.height) * maxH; }
          const fmt = (user.company_logo.split(';')[0].split('/')[1] || 'png').toUpperCase();
          doc.addImage(user.company_logo, fmt === 'JPG' ? 'JPEG' : fmt, margin, 10, w, h);
        } catch (e) { /* never let a bad logo break the PDF */ }
      }
      
      // Header - Center aligned
      doc.setFontSize(24);
      doc.setFont(undefined, 'bold');
      doc.text(currency.currencyCode === 'INR' ? 'TAX INVOICE' : 'INVOICE', pageWidth / 2, yPos, { align: 'center' });
      yPos += 8;
      
      doc.setFontSize(12);
      doc.setFont(undefined, 'normal');
      doc.text(`Invoice #${invoice.invoice_number || invoice.id.slice(0, 8)}`, pageWidth / 2, yPos, { align: 'center' });
      yPos += 6;
      
      if (invoice.invoice_date) {
        doc.setFontSize(9);
        doc.text(`Invoice Date: ${format(new Date(invoice.invoice_date), 'MMMM dd, yyyy')}`, pageWidth / 2, yPos, { align: 'center' });
        yPos += 5;
      }
      
      // Header border
      yPos += 5;
      doc.setLineWidth(1);
      doc.setDrawColor(50);
      doc.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 12;
      
      // Two-column layout: From and Bill To
      const leftColumnX = margin;
      const rightColumnX = pageWidth / 2 + 5;
      const columnStartY = yPos;
      
      // From Section (Left)
      doc.setFontSize(10);
      doc.setFont(undefined, 'bold');
      doc.text('From:', leftColumnX, yPos);
      yPos += 6;
      
      doc.setFontSize(9);
      doc.setFont(undefined, 'bold');
      doc.text(user?.company_name || user?.name || 'Your Company', leftColumnX, yPos);
      yPos += 5;
      
      doc.setFont(undefined, 'normal');
      const sellerAddr = (user?.company_addresses && user.company_addresses[0]) || null;
      if (sellerAddr) {
        if (sellerAddr.address_line1) { doc.text(String(sellerAddr.address_line1), leftColumnX, yPos); yPos += 4; }
        const cityLine = [sellerAddr.city, sellerAddr.state, sellerAddr.postal_code].filter(Boolean).join(', ');
        if (cityLine) { doc.text(cityLine, leftColumnX, yPos); yPos += 4; }
        if (sellerAddr.country) { doc.text(String(sellerAddr.country), leftColumnX, yPos); yPos += 4; }
      }
      if (user?.phone) { doc.text(`Phone: ${user.phone}`, leftColumnX, yPos); yPos += 4; }
      if (user?.email) { doc.text(`Email: ${user.email}`, leftColumnX, yPos); yPos += 4; }
      if (currency.currencyCode === 'INR' && user?.gstin) { doc.text(`GSTIN: ${user.gstin}`, leftColumnX, yPos); yPos += 4; }
      
      // Bill To Section (Right)
      yPos = columnStartY;
      doc.setFontSize(10);
      doc.setFont(undefined, 'bold');
      doc.text('Bill To:', rightColumnX, yPos);
      yPos += 6;
      
      if (client) {
        doc.setFontSize(9);
        doc.setFont(undefined, 'bold');
        doc.text(client.name, rightColumnX, yPos);
        yPos += 5;
        
        doc.setFont(undefined, 'normal');
        if (client.company_name) {
          doc.text(client.company_name, rightColumnX, yPos);
          yPos += 4;
        }
        if (client.email) {
          doc.text(client.email, rightColumnX, yPos);
          yPos += 4;
        }
        if (client.phone) {
          doc.text(client.phone, rightColumnX, yPos);
          yPos += 4;
        }
      }
      
      yPos = Math.max(yPos, columnStartY + 30);
      yPos += 12;
      
      // Invoice Details Section (gray box)
      const detailsBoxY = yPos;
      doc.setFillColor(245, 245, 245);
      doc.rect(margin, yPos, pageWidth - 2 * margin, 18, 'F');
      yPos += 6;
      
      doc.setFontSize(8);
      doc.setFont(undefined, 'bold');
      doc.text('Invoice Date', margin + 5, yPos);
      doc.text('Due Date', margin + 65, yPos);
      doc.text('Payment Terms', margin + 125, yPos);
      yPos += 5;
      
      doc.setFont(undefined, 'normal');
      doc.text(invoice.invoice_date ? format(new Date(invoice.invoice_date), 'MMM dd, yyyy') : '-', margin + 5, yPos);
      doc.text(invoice.due_date ? format(new Date(invoice.due_date), 'MMM dd, yyyy') : 'N/A', margin + 65, yPos);
      doc.text(invoice.payment_terms || 'Net 30', margin + 125, yPos);
      yPos += 12;

      // Place of Supply (statutory GST requirement on Indian tax documents)
      if (isINR && invoice.place_of_supply) {
        doc.setFontSize(8);
        doc.setFont(undefined, 'bold');
        doc.text('Place of Supply:', margin, yPos);
        doc.setFont(undefined, 'normal');
        doc.text(String(invoice.place_of_supply), margin + 32, yPos);
        yPos += 8;
      }
      
      // Combined Items Section
      const hasLineItems = invoice.line_items && invoice.line_items.length > 0;
      const hasInventory = invoice.inventory_items && invoice.inventory_items.length > 0;
      
      if (hasLineItems || hasInventory) {
        doc.setFontSize(10);
        doc.setFont(undefined, 'bold');
        doc.text('Invoice Items', margin, yPos);
        yPos += 8;
        
        // Table header
        doc.setLineWidth(1);
        doc.setDrawColor(180);
        doc.line(margin, yPos, pageWidth - margin, yPos);
        yPos += 5;
        
        doc.setFontSize(8);
        doc.setFont(undefined, 'bold');
        const colQtyX = pageWidth - 100;
        const colPriceX = pageWidth - 72;
        const colTaxX = pageWidth - 42;
        const colTotalX = pageWidth - margin - 2;
        const colHsnX = pageWidth - 128;
        doc.text('Item', margin + 2, yPos);
        if (isINR) doc.text('HSN/SAC', colHsnX, yPos, { align: 'right' });
        doc.text('Qty', colQtyX, yPos, { align: 'right' });
        doc.text('Unit Price', colPriceX, yPos, { align: 'right' });
        doc.text(isINR ? 'GST' : 'Tax', colTaxX, yPos, { align: 'right' });
        doc.text('Total', colTotalX, yPos, { align: 'right' });
        yPos += 2;
        
        doc.setLineWidth(0.5);
        doc.line(margin, yPos, pageWidth - margin, yPos);
        yPos += 5;
        
        // Line items
        if (hasLineItems) {
          doc.setFont(undefined, 'normal');
          invoice.line_items.forEach((item) => {
            if (yPos > 260) {
              doc.addPage();
              yPos = 20;
            }
            const maxDesc = isINR ? 26 : 40;
            const description = item.description.length > maxDesc ? item.description.substring(0, maxDesc - 3) + '...' : item.description;
            doc.text(description, margin + 2, yPos);
            if (isINR) doc.text(String(item.hsn_sac || '-'), colHsnX, yPos, { align: 'right' });
            doc.text(String(item.quantity), colQtyX, yPos, { align: 'right' });
            doc.text(`${pdfSym}${parseFloat(item.price || 0).toFixed(2)}`, colPriceX, yPos, { align: 'right' });
            doc.text(`${pdfSym}${parseFloat(item.tax || 0).toFixed(2)}`, colTaxX, yPos, { align: 'right' });
            doc.text(`${pdfSym}${parseFloat(item.total || 0).toFixed(2)}`, colTotalX, yPos, { align: 'right' });
            yPos += 5;
          });
        }
        
        // Inventory items
        if (hasInventory) {
          doc.setFont(undefined, 'normal');
          invoice.inventory_items.forEach((item) => {
            if (yPos > 260) {
              doc.addPage();
              yPos = 20;
            }
            const itemName = (item.item_name || item.itemId || 'Unknown Item');
            const maxName = isINR ? 26 : 40;
            const displayName = itemName.length > maxName ? itemName.substring(0, maxName - 3) + '...' : itemName;
            doc.text(displayName, margin + 2, yPos);
            if (isINR) doc.text(String(item.hsn_sac || '-'), colHsnX, yPos, { align: 'right' });
            doc.text(String(item.quantity), colQtyX, yPos, { align: 'right' });
            doc.text(`${pdfSym}${parseFloat(item.price || 0).toFixed(2)}`, colPriceX, yPos, { align: 'right' });
            doc.text(`${pdfSym}${parseFloat(item.tax || 0).toFixed(2)}`, colTaxX, yPos, { align: 'right' });
            doc.text(`${pdfSym}${parseFloat(item.total || 0).toFixed(2)}`, colTotalX, yPos, { align: 'right' });
            yPos += 5;
          });
        }
        
        doc.setLineWidth(0.5);
        doc.line(margin, yPos, pageWidth - margin, yPos);
        yPos += 10;
      }
      
      // Totals Section (right-aligned)
      if (yPos > 210) {
        doc.addPage();
        yPos = 20;
      }
      
      yPos += 5;
      const totalsX = pageWidth - 80;
      
      doc.setFontSize(9);
      doc.setFont(undefined, 'bold');
      doc.text('Subtotal:', totalsX, yPos);
      doc.text(`${pdfSym}${(invoice.subtotal || 0).toFixed(2)}`, pageWidth - margin - 2, yPos, { align: 'right' });
      yPos += 6;
      
      // GST split: CGST+SGST (intra-state) or IGST (inter-state); fall back to a single
      // Tax/GST line for legacy docs or non-GST sellers (no split stored).
      const _cgst = invoice.gst_cgst || 0, _sgst = invoice.gst_sgst || 0, _igst = invoice.gst_igst || 0;
      if (isINR && (_cgst > 0 || _sgst > 0 || _igst > 0)) {
        if (_cgst > 0 || _sgst > 0) {
          doc.text('CGST:', totalsX, yPos);
          doc.text(`${pdfSym}${_cgst.toFixed(2)}`, pageWidth - margin - 2, yPos, { align: 'right' });
          yPos += 6;
          doc.text('SGST:', totalsX, yPos);
          doc.text(`${pdfSym}${_sgst.toFixed(2)}`, pageWidth - margin - 2, yPos, { align: 'right' });
          yPos += 6;
        }
        if (_igst > 0) {
          doc.text('IGST:', totalsX, yPos);
          doc.text(`${pdfSym}${_igst.toFixed(2)}`, pageWidth - margin - 2, yPos, { align: 'right' });
          yPos += 6;
        }
      } else {
        doc.text(isINR ? 'GST:' : 'Tax:', totalsX, yPos);
        doc.text(`${pdfSym}${(invoice.tax_amount || 0).toFixed(2)}`, pageWidth - margin - 2, yPos, { align: 'right' });
        yPos += 6;
      }
      
      if (invoice.discount_amount > 0) {
        doc.text('Discount:', totalsX, yPos);
        doc.text(`-${pdfSym}${(invoice.discount_amount || 0).toFixed(2)}`, pageWidth - margin - 2, yPos, { align: 'right' });
        yPos += 6;
      }
      
      // Grand Total
      doc.setLineWidth(1);
      doc.setDrawColor(50);
      doc.line(totalsX - 2, yPos, pageWidth - margin, yPos);
      yPos += 7;
      
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.text('TOTAL:', totalsX, yPos);
      doc.text(`${pdfSym}${(invoice.total_amount || 0).toFixed(2)}`, pageWidth - margin - 2, yPos, { align: 'right' });
      yPos += 3;
      
      // Payment info
      if (invoice.paid_amount > 0) {
        yPos += 8;
        doc.setFontSize(9);
        doc.setFont(undefined, 'normal');
        doc.text('Amount Paid:', totalsX, yPos);
        doc.text(`${pdfSym}${(invoice.paid_amount || 0).toFixed(2)}`, pageWidth - margin - 2, yPos, { align: 'right' });
        yPos += 6;
        doc.setFont(undefined, 'bold');
        doc.text('Balance Due:', totalsX, yPos);
        doc.text(`${pdfSym}${(invoice.remaining_amount || 0).toFixed(2)}`, pageWidth - margin - 2, yPos, { align: 'right' });
      }
      
      // Amount in words
      const amountWords = invoice.total_in_words || numberToWords(invoice.total_amount || 0, currency.currencyCode);
      if (amountWords) {
        if (yPos > 250) { doc.addPage(); yPos = 20; }
        yPos += 9;
        doc.setFontSize(8);
        doc.setFont(undefined, 'bold');
        doc.text('Amount in Words:', margin, yPos);
        yPos += 4;
        doc.setFont(undefined, 'normal');
        const wrappedWords = doc.splitTextToSize(String(amountWords), pageWidth - 2 * margin);
        doc.text(wrappedWords, margin, yPos);
        yPos += wrappedWords.length * 4;
      }
      
      // Notes and Terms
      if (invoice.notes || invoice.terms) {
        yPos += 15;
        if (yPos > 240) {
          doc.addPage();
          yPos = 20;
        }
        
        if (invoice.notes) {
          doc.setFontSize(8);
          doc.setFont(undefined, 'bold');
          doc.text('Notes:', margin, yPos);
          yPos += 4;
          doc.setFont(undefined, 'normal');
          const splitNotes = doc.splitTextToSize(invoice.notes, pageWidth - 2 * margin);
          doc.text(splitNotes, margin, yPos);
          yPos += (splitNotes.length * 4) + 6;
        }
        
        if (invoice.terms) {
          if (yPos > 240) {
            doc.addPage();
            yPos = 20;
          }
          doc.setFontSize(8);
          doc.setFont(undefined, 'bold');
          doc.text('Terms & Conditions:', margin, yPos);
          yPos += 4;
          doc.setFont(undefined, 'normal');
          const splitTerms = doc.splitTextToSize(invoice.terms, pageWidth - 2 * margin);
          doc.text(splitTerms, margin, yPos);
        }
      }
      
      // Footer on all pages
      const pageCount = doc.internal.pages.length - 1;
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setLineWidth(0.5);
        doc.setDrawColor(180);
        doc.line(margin, pageHeight - 15, pageWidth - margin, pageHeight - 15);
        doc.setFontSize(7);
        doc.setFont(undefined, 'normal');
        doc.text('Thank you for your business!', pageWidth / 2, pageHeight - 8, { align: 'center' });
        doc.setFontSize(6);
        doc.text(`Page ${i} of ${pageCount}`, pageWidth - margin - 2, pageHeight - 8, { align: 'right' });
      }
      
      // Save the PDF
      doc.save(`invoice-${invoice.invoice_number || invoice.id.slice(0, 8)}.pdf`);
      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error('Failed to generate PDF:', error);
      toast.error('Failed to generate PDF');
    }
  };

  const handleAddPayment = async (e) => {
    e.preventDefault();
    
    // Validate payment amount
    const currentRemaining = invoice.remaining_amount || invoice.total_amount || 0;
    
    if (parseFloat(paymentForm.amount) > currentRemaining) {
      toast.error(`Payment amount (${currency.format(parseFloat(paymentForm.amount) || 0)}) exceeds remaining balance (${currency.format(currentRemaining)})`);
      return;
    }
    
    if (parseFloat(paymentForm.amount) <= 0) {
      toast.error('Payment amount must be greater than 0');
      return;
    }
    
    try {
      // Call backend API to add payment
      await invoicesAPI.addPayment(id, {
        amount: parseFloat(paymentForm.amount),
        payment_method: paymentForm.payment_method,
        payment_date: paymentForm.payment_date,
        notes: paymentForm.notes
      });
      
      // Save activity to backend
      const activityData = {
        invoice_id: id,
        activity_type: 'payment',
        description: `Payment of ${currency.format(parseFloat(paymentForm.amount) || 0)} received via ${paymentForm.payment_method}`,
        metadata: {
          amount: parseFloat(paymentForm.amount),
          payment_method: paymentForm.payment_method
        }
      };
      
      try {
        await invoicesAPI.createActivity(id, activityData);
      } catch (error) {
        console.error('Failed to save activity:', error);
      }
      
      // Reload invoice and payments
      await loadInvoice();
      
      toast.success('Payment added successfully');
      setPaymentDialogOpen(false);
      
      // Reset form
      setPaymentForm({
        amount: '',
        payment_method: 'cash',
        payment_date: format(new Date(), 'yyyy-MM-dd'),
        notes: ''
      });
    } catch (error) {
      console.error('Failed to add payment:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to add payment';
      toast.error(errorMsg);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4" data-testid="invoice-details-loading">
        <div className="h-8 w-64 bg-muted animate-pulse rounded"></div>
        <div className="grid grid-cols-3 gap-4">
          <div className="col-span-2 h-96 bg-muted animate-pulse rounded"></div>
          <div className="h-96 bg-muted animate-pulse rounded"></div>
        </div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="space-y-4" data-testid="invoice-not-found">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/invoices')} data-testid="back-button">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Invoices
          </Button>
        </div>
        <Card className="p-8 text-center">
          <CardContent>
            <h2 className="text-2xl font-bold mb-2">Invoice Not Found</h2>
            <p className="text-muted-foreground mb-4">
              The invoice you're looking for doesn't exist or you don't have permission to view it.
            </p>
            <Button onClick={() => navigate('/invoices')}>
              Go Back to Invoices
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const subtotal = invoice?.subtotal || 0;
  const taxAmount = invoice?.tax_amount || 0;
  const discountAmount = invoice?.discount_amount || 0;
  const totalAmount = invoice?.total_amount || 0;
  const displayLineItems = invoice?.line_items || [];
  const displayInventoryItems = invoice?.inventory_items || [];

  return (
    <div className="space-y-6" data-testid="invoice-details-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/invoices')} data-testid="back-button">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Invoices
          </Button>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              Invoice #{invoice.invoice_number || invoice.id.slice(0, 8)}
            </h1>
            <p className="text-muted-foreground">
              Created on {invoice.invoice_date ? format(new Date(invoice.invoice_date), 'MMMM dd, yyyy') : '-'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge 
            style={{
              backgroundColor: invoiceStatuses.find(s => s.name.toLowerCase() === invoice.status?.toLowerCase())?.color || '#gray',
              color: '#fff'
            }}
            data-testid="invoice-status"
          >
            {invoiceStatuses.find(s => s.name.toLowerCase() === invoice.status?.toLowerCase())?.name || 
             invoice.status?.charAt(0).toUpperCase() + invoice.status?.slice(1)}
          </Badge>
          {canUpdate() && invoice.status?.toLowerCase() !== 'paid' && (
            <Button variant="outline" size="sm" onClick={handleEdit} data-testid="edit-invoice-button">
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={handleDownloadPDF} data-testid="download-invoice-button">
            <Download className="h-4 w-4 mr-2" />
            Download PDF
          </Button>
          {invoice.status === 'draft' && (
            <Button size="sm" onClick={() => handleStatusChange('sent')} data-testid="send-invoice-button">
              <Send className="h-4 w-4 mr-2" />
              Send to Client
            </Button>
          )}
          {hasPermission('account', 'payment', 'create') && (invoice.status === 'accepted' || invoice.status === 'sent') && (
            <Button size="sm" onClick={() => setPaymentDialogOpen(true)} data-testid="add-payment-button">
              <CreditCard className="h-4 w-4 mr-2" />
              Add Payment
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {invoice.quote_id && invoice.quote_number && (
            <Card className="bg-gray-100 dark:bg-gray-800 border-gray-200 dark:border-gray-700">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Linked Quote
                </CardTitle>
              </CardHeader>
              <CardContent>
                <button
                  onClick={() => window.open(`/quotes/${invoice.quote_id}`, '_blank')}
                  className="text-lg font-semibold text-blue-600 hover:text-blue-800 hover:underline"
                  data-testid="linked-quote-button"
                >
                  Quote #{invoice.quote_number}
                </button>
                {invoice.quote_paid_amount > 0 && (
                  <p className="text-sm text-muted-foreground mt-2">
                    Payment Collected: {currency.format(invoice.quote_paid_amount)}
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {invoice.job_id && invoice.job_number && (
            <Card className="bg-gray-100 dark:bg-gray-800 border-gray-200 dark:border-gray-700">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Linked Job
                </CardTitle>
              </CardHeader>
              <CardContent>
                <button
                  onClick={() => window.open(`/jobs/${invoice.job_id}`, '_blank')}
                  className="text-lg font-semibold text-blue-600 hover:text-blue-800 hover:underline"
                  data-testid="linked-job-button"
                >
                  Job #{invoice.job_number}
                </button>
                {invoice.job_paid_amount > 0 && (
                  <p className="text-sm text-muted-foreground mt-2">
                    Payment Collected: {currency.format(invoice.job_paid_amount)}
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Client Information</CardTitle>
            </CardHeader>
            <CardContent>
              {client ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Name</p>
                      <p className="font-medium">{client.name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Email</p>
                      <p className="font-medium">{client.email}</p>
                    </div>
                    {client.phone && (
                      <div>
                        <p className="text-sm text-muted-foreground">Phone</p>
                        <p className="font-medium">{client.phone}</p>
                      </div>
                    )}
                    {client.company_name && (
                      <div>
                        <p className="text-sm text-muted-foreground">Company</p>
                        <p className="font-medium">{client.company_name}</p>
                      </div>
                    )}
                  </div>
                  {/* Display address from addresses array */}
                  {client.addresses && client.addresses.length > 0 && (
                    <div className="pt-2 border-t">
                      <p className="text-sm text-muted-foreground mb-2">Address</p>
                      <p className="font-medium text-sm leading-relaxed">
                        {[
                          client.addresses[0].address_line1,
                          client.addresses[0].address_line2,
                          client.addresses[0].city,
                          client.addresses[0].state,
                          client.addresses[0].postal_code
                        ].filter(Boolean).join(', ')}
                      </p>
                    </div>
                  )}
                  {/* Display address from flat fields if addresses array not available */}
                  {(!client.addresses || client.addresses.length === 0) && (client.address || client.city || client.state || client.zip_code) && (
                    <div className="pt-2 border-t">
                      <p className="text-sm text-muted-foreground mb-2">Address</p>
                      <p className="font-medium text-sm leading-relaxed">
                        {[
                          client.address,
                          client.city,
                          client.state,
                          client.zip_code,
                          client.country
                        ].filter(Boolean).join(', ')}
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-muted-foreground">Client ID: {invoice.client_id}</p>
              )}
            </CardContent>
          </Card>

          {displayLineItems.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Line Items</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-muted">
                      <tr>
                        <th className="text-left p-3 text-sm font-medium">Description</th>
                        <th className="text-right p-3 text-sm font-medium w-20">Qty</th>
                        <th className="text-right p-3 text-sm font-medium w-24">Price</th>
                        <th className="text-right p-3 text-sm font-medium w-24">Tax</th>
                        <th className="text-right p-3 text-sm font-medium w-24">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayLineItems.map((item, index) => (
                        <tr key={index} className="border-t">
                          <td className="p-3">{item.description}</td>
                          <td className="p-3 text-right">{item.quantity}</td>
                          <td className="p-3 text-right">{currency.format(parseFloat(item.price || 0))}</td>
                          <td className="p-3 text-right">{currency.format(parseFloat(item.tax || 0))}</td>
                          <td className="p-3 text-right font-medium">{currency.format(parseFloat(item.total || 0))}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {displayInventoryItems.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Inventory Items</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-muted">
                      <tr>
                        <th className="text-left p-3 text-sm font-medium">Product</th>
                        <th className="text-right p-3 text-sm font-medium w-20">Qty</th>
                        <th className="text-right p-3 text-sm font-medium w-24">Price</th>
                        <th className="text-right p-3 text-sm font-medium w-24">Tax</th>
                        <th className="text-right p-3 text-sm font-medium w-24">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayInventoryItems.map((item, index) => (
                        <tr key={index} className="border-t">
                          <td className="p-3">{item.item_name || item.itemId || 'Unknown Item'}</td>
                          <td className="p-3 text-right">{item.quantity}</td>
                          <td className="p-3 text-right">{currency.format(parseFloat(item.price || 0))}</td>
                          <td className="p-3 text-right">{currency.format(parseFloat(item.tax || 0))}</td>
                          <td className="p-3 text-right font-medium">{currency.format(parseFloat(item.total || 0))}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {invoice.instructions && (
            <Card>
              <CardHeader>
                <CardTitle>Instructions</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm whitespace-pre-wrap">{invoice.instructions}</p>
              </CardContent>
            </Card>
          )}

          <div className="grid grid-cols-2 gap-4">
            {invoice.terms && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Terms & Conditions</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm whitespace-pre-wrap">{invoice.terms}</p>
                </CardContent>
              </Card>
            )}
            {invoice.notes && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Internal Notes</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm whitespace-pre-wrap text-muted-foreground">{invoice.notes}</p>
                </CardContent>
              </Card>
            )}
          </div>

          {activities.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Recent Activity
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {activities.slice(-5).reverse().map((activity) => (
                  <div key={activity.id} className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                    <div className="flex-1">
                      <p className="text-sm">{activity.description}</p>
                      <p className="text-xs text-muted-foreground">
                        {activity.created_at || activity.timestamp ? 
                          format(new Date(activity.created_at || activity.timestamp), 'MMM dd, yyyy HH:mm') 
                          : 'N/A'}
                      </p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Invoice Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm text-muted-foreground">Invoice Date</p>
                <p className="font-medium">
                  {invoice.invoice_date ? format(new Date(invoice.invoice_date), 'MMM dd, yyyy') : '-'}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Expiry Date</p>
                <p className="font-medium">
                  {invoice.expiry_date ? format(new Date(invoice.expiry_date), 'MMM dd, yyyy') : 'No expiry'}
                </p>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground">Change Status</Label>
                <Select 
                  value={invoice.status?.toLowerCase()} 
                  onValueChange={handleStatusChange}
                  disabled={invoice.status?.toLowerCase() === 'paid'}
                >
                  <SelectTrigger className="mt-1" data-testid="status-selector">
                    <SelectValue>
                      {invoice.status ? (
                        invoiceStatuses.find(s => s.name.toLowerCase() === invoice.status?.toLowerCase())?.name || 
                        invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)
                      ) : 'Select Status'}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {invoiceStatuses.map((status) => (
                      <SelectItem key={status.id} value={status.name.toLowerCase()}>
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-2 h-2 rounded-full" 
                            style={{ backgroundColor: status.color || '#gray' }}
                          ></div>
                          <span>{status.name}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {invoice.status?.toLowerCase() === 'paid' && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Paid invoices cannot be modified
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Financial Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Subtotal</span>
                <span className="font-medium">{currency.format(subtotal)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">
                  Tax {invoice.tax_name ? `(${invoice.tax_name})` : ''}
                </span>
                <span className="font-medium">{currency.format(taxAmount)}</span>
              </div>
              {discountAmount > 0 && (
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Discount</span>
                  <span className="font-medium text-green-600">-{currency.format(discountAmount)}</span>
                </div>
              )}
              <Separator />
              <div className="flex justify-between items-center">
                <span className="text-lg font-bold">Total Amount</span>
                <span className="text-2xl font-bold text-primary">{currency.format(totalAmount)}</span>
              </div>
              {invoice.total_in_words && (
                <div className="pt-2 border-t">
                  <p className="text-xs text-muted-foreground">Amount in Words:</p>
                  <p className="text-sm font-medium italic mt-1">{invoice.total_in_words}</p>
                </div>
              )}
              {invoice.deposit_required > 0 && (
                <>
                  <Separator />
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">Required Deposit</span>
                    <span className="text-lg font-bold text-orange-600">
                      {currency.format(parseFloat(invoice.deposit_required))}
                    </span>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Tax Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {invoice.tax_name && (
                <div>
                  <p className="text-sm text-muted-foreground">Tax Type</p>
                  <p className="font-medium">{invoice.tax_name}</p>
                </div>
              )}
              {invoice.tax_rate > 0 && (
                <div>
                  <p className="text-sm text-muted-foreground">Tax Rate</p>
                  <p className="font-medium">{invoice.tax_rate}%</p>
                </div>
              )}
              {(!invoice.tax_name || invoice.tax_rate === 0) && (
                <p className="text-sm text-muted-foreground">No tax applied</p>
              )}
            </CardContent>
          </Card>

          {(invoice.quote_id && invoice.quote_paid_amount > 0) && (
            <Card className="border-blue-200 dark:border-blue-900">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-blue-600 dark:text-blue-400">
                  <DollarSign className="h-4 w-4" />
                  Quote Payment (Applied to Invoice)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="p-3 bg-blue-50 dark:bg-blue-950 rounded-lg">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm text-muted-foreground">From Quote #{invoice.quote_number}</p>
                      <p className="font-medium">Payment collected on quote</p>
                    </div>
                    <span className="font-bold text-blue-600 dark:text-blue-400">
                      {currency.format(parseFloat(invoice.quote_paid_amount || 0))}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {(payments.length > 0 || invoice.paid_amount > 0) && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-4 w-4" />
                  Payments Received
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {payments && payments.length > 0 ? (
                  <>
                    {payments.map((payment) => (
                      <div key={payment.id} className="flex justify-between items-center p-3 bg-muted rounded-lg">
                        <div>
                          <p className="font-medium">{currency.format(parseFloat(payment.amount))}</p>
                          <p className="text-sm text-muted-foreground capitalize">
                            {payment.payment_method} • {format(new Date(payment.payment_date), 'MMM dd, yyyy')}
                          </p>
                          {payment.notes && (
                            <p className="text-xs text-muted-foreground mt-1">{payment.notes}</p>
                          )}
                        </div>
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      </div>
                    ))}
                    <div className="pt-2 border-t space-y-2">
                      {invoice.quote_paid_amount > 0 && (
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-muted-foreground">Quote Payment:</span>
                          <span className="text-blue-600 dark:text-blue-400">
                            {currency.format(parseFloat(invoice.quote_paid_amount || 0))}
                          </span>
                        </div>
                      )}
                      {invoice.job_paid_amount > 0 && (
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-muted-foreground">Job Payment:</span>
                          <span className="text-purple-600 dark:text-purple-400">
                            {currency.format(parseFloat(invoice.job_paid_amount || 0))}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium">Total Paid (Invoice):</span>
                        <span className="font-bold text-green-600">
                          {currency.format(payments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0))}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium">Total Paid (All Sources):</span>
                        <span className="font-bold text-green-600">
                          {currency.format(payments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0) + parseFloat(invoice.quote_paid_amount || 0) + parseFloat(invoice.job_paid_amount || 0))}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">Remaining:</span>
                        <span className="font-bold text-orange-600">
                          {currency.format(totalAmount - payments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0) - parseFloat(invoice.quote_paid_amount || 0) - parseFloat(invoice.job_paid_amount || 0))}
                        </span>
                      </div>
                    </div>
                  </>
                ) : (
                  <p className="text-center text-muted-foreground py-4">No invoice payments received yet</p>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Edit Invoice Sheet */}
      {invoice && (
        <Sheet open={editSheetOpen} onOpenChange={setEditSheetOpen}>
          <SheetContent side="right" className="w-full sm:max-w-6xl overflow-y-auto" data-testid="edit-invoice-sheet">
            <SheetHeader>
              <SheetTitle>Edit Invoice #{invoice.invoice_number}</SheetTitle>
            </SheetHeader>
          <form onSubmit={handleUpdateInvoice} className="space-y-6 mt-6">
              {/* Basic Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Basic Information</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2">
                    <Label>Client Name</Label>
                    <Input
                      value={client?.name || invoice?.client_name || editForm.client_id}
                      disabled
                      data-testid="edit-client-name"
                    />
                  </div>
                  <div>
                    <Label>Invoice Number</Label>
                    <Input
                      value={editForm.invoice_number}
                      onChange={(e) => setEditForm({ ...editForm, invoice_number: e.target.value })}
                      data-testid="edit-invoice-number"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Invoice Date *</Label>
                    <Input
                      type="date"
                      value={editForm.invoice_date}
                      onChange={(e) => setEditForm({ ...editForm, invoice_date: e.target.value })}
                      required
                      data-testid="edit-invoice-date"
                    />
                  </div>
                  <div>
                    <Label>Expiry Date</Label>
                    <Input
                      type="date"
                      value={editForm.expiry_date}
                      onChange={(e) => setEditForm({ ...editForm, expiry_date: e.target.value })}
                      data-testid="edit-expiry-date"
                    />
                  </div>
                  <div>
                    <Label>Status</Label>
                    <Select value={editForm.status} onValueChange={(value) => setEditForm({ ...editForm, status: value })}>
                      <SelectTrigger data-testid="edit-status">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {invoiceStatuses.map((status) => (
                          <SelectItem key={status.id} value={status.name.toLowerCase()}>
                            {status.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Line Items */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">Line Items</h3>
                  <Button type="button" variant="outline" size="sm" onClick={handleAddLineItem}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Line Item
                  </Button>
                </div>
                {lineItems.length > 0 && (
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-muted">
                        <tr>
                          <th className="text-left p-2 text-sm font-medium">Description</th>
                          <th className="text-left p-2 text-sm font-medium w-20">Qty</th>
                          <th className="text-left p-2 text-sm font-medium w-24">Price</th>
                          <th className="text-left p-2 text-sm font-medium w-24">Tax</th>
                          <th className="text-left p-2 text-sm font-medium w-24">Total</th>
                          <th className="w-10"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {lineItems.map((item) => (
                          <tr key={item.id} className="border-t">
                            <td className="p-2">
                              <Input
                                placeholder="Item description"
                                value={item.description}
                                onChange={(e) => handleUpdateLineItem(item.id, 'description', e.target.value)}
                                className="h-8"
                              />
                            </td>
                            <td className="p-2">
                              <Input
                                type="tel" inputMode="decimal" onInput={(e) => { e.target.value = e.target.value.replace(/[^0-9.]/g, ""); }}
                                value={item.quantity}
                                onChange={(e) => handleUpdateLineItem(item.id, 'quantity', parseFloat(e.target.value) || 0)}
                                className="h-8"
                                min="0"
                              />
                            </td>
                            <td className="p-2">
                              <Input
                                type="tel" inputMode="decimal" onInput={(e) => { e.target.value = e.target.value.replace(/[^0-9.]/g, ""); }}
                                value={item.price}
                                onChange={(e) => handleUpdateLineItem(item.id, 'price', parseFloat(e.target.value) || 0)}
                                className="h-8"
                                min="0"
                                step="0.01"
                              />
                            </td>
                            <td className="p-2">
                              <Input
                                type="tel" inputMode="decimal" onInput={(e) => { e.target.value = e.target.value.replace(/[^0-9.]/g, ""); }}
                                value={item.tax}
                                onChange={(e) => handleUpdateLineItem(item.id, 'tax', parseFloat(e.target.value) || 0)}
                                className="h-8"
                                min="0"
                                step="0.01"
                              />
                            </td>
                            <td className="p-2">
                              <span className="text-sm font-medium">{currency.format(item.total)}</span>
                            </td>
                            <td className="p-2">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveLineItem(item.id)}
                                className="h-8 w-8 p-0"
                              >
                                <X className="h-4 w-4 text-destructive" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Inventory Items */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">Inventory Items</h3>
                  <Button type="button" variant="outline" size="sm" onClick={handleAddInventoryItem}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Product
                  </Button>
                </div>
                {inventoryItems.length > 0 && (
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-muted">
                        <tr>
                          <th className="text-left p-2 text-sm font-medium">Product</th>
                          <th className="text-left p-2 text-sm font-medium w-20">Qty</th>
                          <th className="text-left p-2 text-sm font-medium w-24">Price</th>
                          <th className="text-left p-2 text-sm font-medium w-20">Tax %</th>
                          <th className="text-left p-2 text-sm font-medium w-24">Total</th>
                          <th className="w-10"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {inventoryItems.map((item) => (
                          <tr key={item.id} className="border-t">
                            <td className="p-2">
                              <Select
                                value={item.itemId.toString()}
                                onValueChange={(value) => handleUpdateInventoryItem(item.id, 'itemId', value)}
                              >
                                <SelectTrigger className="h-8">
                                  <SelectValue placeholder="Select product" />
                                </SelectTrigger>
                                <SelectContent>
                                  {availableInventory.map((invItem) => (
                                    <SelectItem key={invItem.id} value={invItem.id}>
                                      {invItem.name} (${invItem.selling_price || invItem.price || 0})
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </td>
                            <td className="p-2">
                              <Input
                                type="tel" inputMode="decimal" onInput={(e) => { e.target.value = e.target.value.replace(/[^0-9.]/g, ""); }}
                                value={item.quantity}
                                onChange={(e) => handleUpdateInventoryItem(item.id, 'quantity', parseFloat(e.target.value) || 0)}
                                className="h-8"
                                min="0"
                              />
                            </td>
                            <td className="p-2">
                              <Input
                                type="tel" inputMode="decimal" onInput={(e) => { e.target.value = e.target.value.replace(/[^0-9.]/g, ""); }}
                                value={item.price}
                                className="h-8"
                                disabled
                              />
                            </td>
                            <td className="p-2">
                              <Input
                                type="tel" inputMode="decimal" onInput={(e) => { e.target.value = e.target.value.replace(/[^0-9.]/g, ""); }}
                                value={item.taxRate}
                                onChange={(e) => handleUpdateInventoryItem(item.id, 'taxRate', parseFloat(e.target.value) || 0)}
                                className="h-8"
                                min="0"
                                max="100"
                                step="0.1"
                              />
                            </td>
                            <td className="p-2">
                              <span className="text-sm font-medium">{currency.format(item.total)}</span>
                            </td>
                            <td className="p-2">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveInventoryItem(item.id)}
                                className="h-8 w-8 p-0"
                              >
                                <X className="h-4 w-4 text-destructive" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Taxes & Discounts */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Taxes & Discounts</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Tax</Label>
                    <Select
                      value={`${editForm.tax_rate || 0}`}
                      onValueChange={(value) => {
                        const selectedTax = taxes.find(t => t.rate.toString() === value);
                        if (selectedTax) {
                          setEditForm({ ...editForm, tax_rate: selectedTax.rate, tax_name: selectedTax.name });
                        } else if (value === '0') {
                          setEditForm({ ...editForm, tax_rate: 0, tax_name: 'No Tax' });
                        }
                      }}
                    >
                      <SelectTrigger data-testid="tax-select">
                        <SelectValue placeholder="Select tax" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">No Tax</SelectItem>
                        {taxes.map((tax) => (
                          <SelectItem key={tax.id} value={tax.rate.toString()}>
                            {tax.name} ({tax.rate}%)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div></div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Discount Type</Label>
                    <Select value={editForm.discount_type} onValueChange={(value) => setEditForm({ ...editForm, discount_type: value })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="percentage">Percentage</SelectItem>
                        <SelectItem value="fixed">Fixed Amount</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Discount Value</Label>
                    <Input
                      type="tel" inputMode="decimal" onInput={(e) => { e.target.value = e.target.value.replace(/[^0-9.]/g, ""); }}
                      value={editForm.discount_value}
                      onChange={(e) => setEditForm({ ...editForm, discount_value: parseFloat(e.target.value) || 0 })}
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div>
                    <Label>Required Deposit ($)</Label>
                    <Input
                      type="tel" inputMode="decimal" onInput={(e) => { e.target.value = e.target.value.replace(/[^0-9.]/g, ""); }}
                      value={editForm.deposit_required}
                      onChange={(e) => setEditForm({ ...editForm, deposit_required: parseFloat(e.target.value) || 0 })}
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>
              </div>

              {/* Totals Summary */}
              {(lineItems.length > 0 || inventoryItems.length > 0) && (
                <Card>
                  <CardContent className="p-4">
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Subtotal:</span>
                        <span className="font-medium">{currency.format(calculateSubtotal())}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Tax:</span>
                        <span className="font-medium">{currency.format(calculateTax())}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Discount:</span>
                        <span className="font-medium text-green-600">-{currency.format(calculateDiscount())}</span>
                      </div>
                      <div className="flex justify-between pt-2 border-t">
                        <span className="text-lg font-bold">Grand Total:</span>
                        <span className="text-lg font-bold text-primary">{currency.format(calculateGrandTotal())}</span>
                      </div>
                      <div className="pt-2 border-t">
                        <p className="text-sm text-muted-foreground">Amount in Words:</p>
                        <p className="text-sm font-medium italic">{numberToWords(calculateGrandTotal(), currency.currencyCode)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Instructions and Notes */}
              <div className="space-y-4">
                <div>
                  <Label>Instructions</Label>
                  <Textarea
                    placeholder="Special instructions..."
                    value={editForm.instructions}
                    onChange={(e) => setEditForm({ ...editForm, instructions: e.target.value })}
                    rows={3}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Internal Notes</Label>
                    <Textarea
                      placeholder="Internal notes..."
                      value={editForm.notes}
                      onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                      rows={2}
                    />
                  </div>
                  <div>
                    <Label>Terms & Conditions</Label>
                    <Textarea
                      placeholder="Terms..."
                      value={editForm.terms}
                      onChange={(e) => setEditForm({ ...editForm, terms: e.target.value })}
                      rows={2}
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setEditSheetOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="update-invoice-button">
                  Update Invoice
                </Button>
              </div>
            </form>
          </SheetContent>
        </Sheet>
      )}

      {/* Add Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent className="max-w-md" data-testid="add-payment-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Add Payment
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddPayment} className="space-y-4">
            <div>
              <Label>Payment Amount *</Label>
              <Input
                type="text"
                inputMode="decimal"
                placeholder="0.00"
                value={paymentForm.amount}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9.]/g, '');
                  // Prevent multiple decimal points
                  const parts = value.split('.');
                  const sanitized = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                  setPaymentForm({ ...paymentForm, amount: sanitized });
                }}
                required
                data-testid="payment-amount"
              />
            </div>
            <div>
              <Label>Payment Method *</Label>
              <Select 
                value={paymentForm.payment_method} 
                onValueChange={(value) => setPaymentForm({ ...paymentForm, payment_method: value })}
              >
                <SelectTrigger data-testid="payment-method">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="check">Check</SelectItem>
                  <SelectItem value="credit_card">Credit Card</SelectItem>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="paypal">PayPal</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Payment Date *</Label>
              <Input
                type="date"
                value={paymentForm.payment_date}
                onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })}
                required
                data-testid="payment-date"
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                placeholder="Payment notes or reference..."
                value={paymentForm.notes}
                onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                rows={2}
                data-testid="payment-notes"
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setPaymentDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="add-payment-submit">
                Add Payment
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
